export class SigningCurve {
    readonly dom: SVGPathElement;
    private path: { x: number; y: number }[];

    constructor(x: number, y: number) {
        this.dom = document.createElementNS(
            'http://www.w3.org/2000/svg',
            'path'
        );
        this.dom.setAttribute('fill', 'none');
        this.dom.setAttribute('stroke', '#666666');
        this.dom.setAttribute('stroke-width', '3');
        this.dom.setAttribute('stroke-linecap', 'round');
        this.path = [{ x, y }];
        this.render();
    }

    private render() {
        let d = 'M' + this.path.map((p) => p.x + ',' + p.y).join(' L');
        this.dom.setAttribute('d', d);
    }

    move(x: number, y: number) {
        let last = this.path[this.path.length - 1];
        if (last.x === x && last.y === y) return;
        this.path.push({ x, y });
        this.render();
    }
}

type AdjustPosition = {
    x: number;
    y: number;
    out: boolean;
};

export default class SigningSvg {
    constructor(public readonly dom: SVGSVGElement) {
        this.dom.addEventListener('touchstart', (ev) => {
            let pos = this.getTouchEventPosition(ev);
            this.start(pos);
        });
        this.dom.addEventListener('mousedown', (ev) => {
            let pos = this.getMouseEventPosition(ev);
            this.start(pos);
        });
        this.dom.addEventListener('touchmove', (ev) => {
            ev.preventDefault();
            let pos = this.getTouchEventPosition(ev);
            this.move(pos);
        });
        this.dom.addEventListener('mousemove', (ev) => {
            let pos = this.getMouseEventPosition(ev);
            this.move(pos);
        });
        this.dom.addEventListener('touchend', (ev) => this.end());
        this.dom.addEventListener('touchcancel', (ev) => this.end());
        this.dom.addEventListener('mouseup', (ev) => this.end());
        this.dom.addEventListener('mouseleave', (ev) => this.end());
    }

    async open(html: string): Promise<string> {
        this.dom.innerHTML = html;
        return this.dom.innerHTML;
    }

    private currentPath?: SigningCurve;
    start(pos: AdjustPosition) {
        if (!this.currentPath) {
            this.currentPath = new SigningCurve(pos.x, pos.y);
            this.dom.appendChild(this.currentPath.dom);
        }
        this.move(pos);
    }
    move(pos: AdjustPosition) {
        this.currentPath?.move(pos.x, pos.y);
        if (pos.out) this.end();
    }
    end() {
        this.currentPath = undefined;
    }
    clear() {
        this.dom.innerHTML = '';
    }

    getMouseEventPosition(ev: MouseEvent): AdjustPosition {
        const x = ev.clientX;
        const y = ev.clientY;
        return this.getAdjustedPosition(x, y);
    }
    getTouchEventPosition(ev: TouchEvent): AdjustPosition {
        const x = ev.changedTouches[0].clientX;
        const y = ev.changedTouches[0].clientY;
        return this.getAdjustedPosition(x, y);
    }
    getAdjustedPosition(px: number, py: number): AdjustPosition {
        const clientRect = this.dom.getBoundingClientRect();
        const svgRect = this.dom.viewBox.baseVal;
        let out = false;
        let x =
            svgRect.x +
            Math.floor(
                ((px - clientRect.left) * svgRect.width) / clientRect.width
            );
        let y =
            svgRect.y +
            Math.floor(
                ((py - clientRect.top) * svgRect.height) / clientRect.height
            );
        if (x < 0) {
            x = 0;
            out = true;
        }
        if (y < 0) {
            y = 0;
            out = true;
        }
        if (x > svgRect.width) {
            x = svgRect.width;
            out = true;
        }
        if (y > svgRect.height) {
            y = svgRect.height;
            out = true;
        }
        return { x, y, out };
    }
}
